import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MoreVertical, Heart, Plus } from 'lucide-react';
import { createPortal } from 'react-dom';

/**
 * EntityFavoritesMenu Component
 *
 * A 3-dot vertical menu for adding/removing entities to/from favorite lists.
 * Works with businesses, events, and barters.
 *
 * @param {string} entityId - The ID of the entity (business, event, or barter)
 * @param {string} entityType - Type of entity: 'businesses', 'events', or 'barters'
 * @param {string} chatId - User's chat ID
 * @param {Array} favoritesLists - Array of user's favorites lists
 * @param {Function} onToggle - Callback when entity is added/removed from a list
 * @param {Function} onCreateList - Callback to create a new list
 * @param {string} zone - Zone ID (required for context)
 */
export const EntityFavoritesMenu = ({
  entityId,
  entityType,
  chatId,
  favoritesLists = [],
  onToggle,
  onCreateList,
  zone
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newListName, setNewListName] = useState('');
  const buttonRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Check if entity is in any list
  const isInAnyList = () => {
    return favoritesLists.some(list => {
      const items = list.data?.[entityType] || [];
      // Handle both simple IDs and complex objects (groups, posts)
      if (entityType === 'groups') {
        return items.some(item => item.group_id === entityId);
      } else if (entityType === 'posts') {
        return items.some(item => item.post_id === entityId);
      } else {
        return items.includes(entityId);
      }
    });
  };

  // Check if entity is in specific list
  const isInList = (listId) => {
    const list = favoritesLists.find(l => l.playlist_id === listId);
    if (!list) return false;
    const items = list.data?.[entityType] || [];
    // Handle both simple IDs and complex objects (groups, posts)
    if (entityType === 'groups') {
      return items.some(item => item.group_id === entityId);
    } else if (entityType === 'posts') {
      return items.some(item => item.post_id === entityId);
    } else {
      return items.includes(entityId);
    }
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) return;

    await onCreateList(newListName.trim(), entityType, entityId);
    setNewListName('');
    setShowCreateInput(false);
    setIsOpen(false);
  };

  const handleToggleList = async (listId) => {
    await onToggle(listId, entityId, entityType);
  };

  // Update menu position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8, // 8px gap below button
        left: rect.left
      });
    }
  }, [isOpen]);

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      {/* 3-Dot Menu Button */}
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`
          flex items-center justify-center w-8 h-8 rounded-full
          transition-all duration-200
          ${isInAnyList()
            ? 'bg-danger-50 text-danger-500 hover:bg-danger-100'
            : 'bg-white/80 text-ink-muted hover:bg-white hover:text-danger-500'
          }
          shadow-sm hover:shadow-md backdrop-blur-sm
        `}
        title={isInAnyList() ? 'בפייבוריטים' : 'הוסף לפייבוריטים'}
      >
        {isInAnyList() ? (
          <Heart size={16} fill="currentColor" strokeWidth={0} />
        ) : (
          <MoreVertical size={16} />
        )}
      </button>

      {/* Dropdown Menu - Rendered via Portal */}
      {isOpen && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[9998]"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
              setShowCreateInput(false);
            }}
          />

          {/* Menu Panel */}
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'fixed',
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              zIndex: 9999
            }}
            className="w-64 bg-white rounded-lg shadow-xl border border-line overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
              {/* Header */}
              <div className="px-4 py-3 bg-gradient-to-l from-danger-50 to-white border-b border-line-subtle">
                <div className="flex items-center gap-2">
                  <Heart size={16} className="text-danger-500" />
                  <span className="text-sm font-semibold text-ink">רשימות מועדפים</span>
                </div>
              </div>

              {/* Lists */}
              <div className="max-h-64 overflow-y-auto">
                {favoritesLists.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-ink-subtle">
                    אין רשימות עדיין
                  </div>
                ) : (
                  favoritesLists.map(list => (
                    <button
                      key={list.playlist_id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleList(list.playlist_id);
                      }}
                      className="w-full px-4 py-3 text-right hover:bg-surface-muted transition-colors duration-150 border-b border-line-subtle last:border-b-0 flex items-center justify-between gap-2"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`
                          w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200
                          ${isInList(list.playlist_id)
                            ? 'bg-danger-500 border-danger-500'
                            : 'border-line-strong'
                          }
                        `}>
                          {isInList(list.playlist_id) && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="text-sm font-medium text-ink">
                          {list.data?.name || 'ללא שם'}
                        </span>
                      </div>
                      <span className="text-xs text-ink-subtle">
                        {(() => {
                          const count = (list.data?.businesses?.length || 0) +
                                       (list.data?.events?.length || 0) +
                                       (list.data?.barters?.length || 0) +
                                       (list.data?.groups?.length || 0) +
                                       (list.data?.posts?.length || 0);
                          return count === 0 ? '' : `(${count})`;
                        })()}
                      </span>
                    </button>
                  ))
                )}
              </div>

              {/* Create New List */}
              <div className="border-t border-line bg-surface-muted">
                {showCreateInput ? (
                  <div className="p-3 space-y-2">
                    <input
                      type="text"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCreateList();
                        }
                      }}
                      placeholder="שם הרשימה החדשה..."
                      className="w-full px-3 py-2 text-sm border border-line-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-danger-500 text-right"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowCreateInput(false);
                          setNewListName('');
                        }}
                        className="flex-1 px-3 py-1.5 text-xs font-medium text-ink-muted bg-white rounded-lg hover:bg-surface-muted transition-colors"
                      >
                        ביטול
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateList();
                        }}
                        disabled={!newListName.trim()}
                        className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-danger-500 rounded-lg hover:bg-danger-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        צור רשימה
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCreateInput(true);
                    }}
                    className="w-full px-4 py-3 text-right hover:bg-surface-muted transition-colors duration-150 flex items-center gap-2 text-danger-500 font-medium text-sm"
                  >
                    <Plus size={16} />
                    <span>צור רשימה חדשה</span>
                  </button>
                )}
              </div>
            </motion.div>
        </>,
        document.body
      )}
    </div>
  );
};
